import {
  faBook,
  faChartLine,
  faCopy,
  faDatabase,
  faExclamationTriangle,
  faFolderTree,
  faQuestionCircle,
  faUser,
  faUsers
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { intersection } from "lodash";
import PropTypes from "prop-types";
import React from "react";
import { Badge, Nav, NavDropdown, NavLink, Navbar, NavbarBrand } from "react-bootstrap";
import NavbarCollapse from "react-bootstrap/NavbarCollapse";
import { FormattedMessage, injectIntl } from "react-intl";
import { Link } from "react-router-dom";
import {
  EMPTY,
  EVALUATION_IND_VIEWER,
  EVALUATION_PRG_VIEWER,
  EVALUATION_VIEWER,
  INDICATOR_VIEWER,
  LIBRARY_VIEWER,
  MASTER_DATA_VIEWER,
  ONE_ELEMENT,
  PROJECTASSISTANT,
  PROJECTASSISTANT_IND,
  PROJECTLEAD,
  PROJECTLEAD_IND,
  ROLE_EDITOR,
  SURVEY_VIEWER,
  TEMPLATE_VIEWER,
  TEMPORARY_USER,
  USER_VIEWER
} from "../../common/constant";
import Visibility from "../../component/common/VisibleContainer";
import UserUtils from "../../utils/UserUtils";
import { DAADLogo } from "./DAADLogo";
import LanguageSelector from "./LanguageSelectorContainer";

const TextWithBreakpoint = ({ id }) => (
  <span>
    <FormattedMessage id={id} />
  </span>
);

const Header = ({
  clearSurveyCaches,
  currentUsername,
  doLogout,
  intl,
  page,
  roles,
  showHint,
  showMessage,
  showDowntimeNotification,
  navIconsOnly
}) => {
  const temp_user = roles.includes(TEMPORARY_USER);
  const pl = roles.includes(PROJECTLEAD);
  const pa = roles.includes(PROJECTASSISTANT);
  const pl_ind = roles.includes(PROJECTLEAD_IND);
  const pa_ind = roles.includes(PROJECTASSISTANT_IND);
  const hasUserViewerRole = roles.includes(USER_VIEWER);
  const hasMasterDataViewerRole = roles.includes(MASTER_DATA_VIEWER);
  const hasRoleEditorRole = roles.includes(ROLE_EDITOR);
  const canClearSurveyCaches = currentUsername && currentUsername.toLowerCase() === "daad-support@mainlevel.de";

  const shouldDropDownForTemplatesBeRendered =
    intersection([TEMPLATE_VIEWER, INDICATOR_VIEWER], roles).length > ONE_ELEMENT;
  const shouldTemplatesBeRendered =
    intersection([TEMPLATE_VIEWER, INDICATOR_VIEWER, LIBRARY_VIEWER], roles).length > EMPTY;
  const shouldDropDownForAdminBeRendered =
    (hasMasterDataViewerRole ? 1 : 0) + (hasUserViewerRole ? 1 : 0) + (hasRoleEditorRole ? 1 : 0) > 1;

  const isLoggedIn = () => !!(roles && roles.length);

  const links =
    temp_user || pl || pa || pl_ind || pa_ind ? (
      <NavLink to="/userArea/respondentsOverview" eventKey="respondentsOverview">
        <FormattedMessage id="overview.survey" />
      </NavLink>
    ) : (
      <>
        {shouldTemplatesBeRendered && shouldDropDownForTemplatesBeRendered ? (
          <Visibility roles={[TEMPLATE_VIEWER, INDICATOR_VIEWER, LIBRARY_VIEWER]}>
            <NavDropdown
              id="user-area"
              title={
                <>
                  <FontAwesomeIcon
                    icon={faCopy}
                    title={intl.formatMessage({ id: "overview.survey_templates" })}
                    className="me-1"
                    size="lg"
                  />
                  {!navIconsOnly && <TextWithBreakpoint id="overview.survey_templates" />}
                </>
              }
              active={["templates", "indicatorManagement", "questionLibrary"].includes(page)}
            >
              <Visibility roles={[TEMPLATE_VIEWER]}>
                <NavDropdown.Item as={Link} to="/userArea/templates" eventKey="templates">
                  <FormattedMessage id="overview.survey_templates" />
                </NavDropdown.Item>
              </Visibility>
              <Visibility roles={[INDICATOR_VIEWER]}>
                <NavDropdown.Item as={Link} to="/userArea/indicatorManagement" eventKey="indicatorManagement">
                  <FormattedMessage id="indicator.management.page.title" />
                </NavDropdown.Item>
              </Visibility>
              <Visibility roles={[LIBRARY_VIEWER]}>
                <NavDropdown.Item as={Link} to="/userArea/questionLibrary" eventKey="questionLibrary">
                  <FormattedMessage id="question.library" />
                </NavDropdown.Item>
              </Visibility>
            </NavDropdown>
          </Visibility>
        ) : (
          <>
            <Visibility roles={[TEMPLATE_VIEWER]}>
              <NavLink as={Link} to="/userArea/templates" eventKey="templates">
                <FontAwesomeIcon
                    icon={faCopy}
                    title={intl.formatMessage({ id: "overview.survey_templates" })}
                    className="me-1"
                    size="lg"
                  />
                   {!navIconsOnly && <TextWithBreakpoint id="overview.survey_templates" />}
              </NavLink>
            </Visibility>
            <Visibility roles={[INDICATOR_VIEWER]}>
              <NavLink as={Link} to="/userArea/indicatorManagement" eventKey="indicatorManagement">
                <FormattedMessage id="indicator.management.page.title" />
              </NavLink>
            </Visibility>
            <Visibility roles={[LIBRARY_VIEWER]}>
              <NavLink as={Link} to="/userArea/questionLibrary" eventKey="questionLibrary">
                <FormattedMessage id="question.library" />
              </NavLink>
            </Visibility>
          </>
        )}
        <Visibility roles={[SURVEY_VIEWER]}>
          <NavLink as={Link} to="/userArea/respondentsOverview" eventKey="respondentsOverview">
            <FontAwesomeIcon
              icon={faBook}
              title={intl.formatMessage({ id: "overview.survey" })}
              size="lg"
              className="me-1"
            />
            {!navIconsOnly && <TextWithBreakpoint id="overview.survey" />}
          </NavLink>
        </Visibility>
        <Visibility roles={[EVALUATION_VIEWER]}>
          <NavDropdown
            id="reports"
            title={
              <>
                <FontAwesomeIcon
                  icon={faChartLine}
                  title={intl.formatMessage({ id: "overview.report" })}
                  className="me-1"
                  size="lg"
                />
                {!navIconsOnly && <TextWithBreakpoint id="overview.report" />}
              </>
            }
            active={["charts", "collapsibleTable"].includes(page)}
          >
            <Visibility roles={[EVALUATION_IND_VIEWER]}>
              <NavDropdown.Item as={Link} to="/userArea/reports/charts" eventKey="charts">
                <FormattedMessage id="evaluation.tabs.indicators" />
              </NavDropdown.Item>
            </Visibility>
            <Visibility roles={[EVALUATION_PRG_VIEWER]}>
              <NavDropdown.Item as={Link} to="/userArea/reports/tables" eventKey="collapsibleTable">
                <FormattedMessage id="evaluation.tabs.programs" />
              </NavDropdown.Item>
            </Visibility>
          </NavDropdown>
        </Visibility>
        {shouldDropDownForAdminBeRendered ? (
          <Visibility roles={[USER_VIEWER, MASTER_DATA_VIEWER]}>
            <NavDropdown
              id="admin"
              title={
                <>
                  <FontAwesomeIcon
                    icon={faUsers}
                    title={intl.formatMessage({ id: "overview.admin" })}
                    className="me-1"
                    size="lg"
                  />
                  {!navIconsOnly && <TextWithBreakpoint id="overview.admin" />}
                </>
              }
              active={["userManagement", "masterDataManagement", "roleManagement"].includes(page)}
            >
              {hasUserViewerRole && (
                <NavDropdown.Item as={Link} to="/userArea/userManagement" eventKey="userManagement">
                  <FormattedMessage id="overview.users" />
                </NavDropdown.Item>
              )}
              {hasMasterDataViewerRole && (
                <NavDropdown.Item as={Link} to="/userArea/masterDataManagement" eventKey="masterDataManagement">
                  <FormattedMessage id="overview.masterdata" />
                </NavDropdown.Item>
              )}
              {hasRoleEditorRole && (
                <NavDropdown.Item as={Link} to="/userArea/roleManagement" eventKey="roleManagement">
                  <FormattedMessage id="overview.roles" />
                </NavDropdown.Item>
              )}
            </NavDropdown>
          </Visibility>
        ) : (
          <>
            {hasUserViewerRole && (
              <Visibility roles={[USER_VIEWER]}>
                <NavLink as={Link} to="/userArea/userManagement" eventKey="userManagement">
                  <FormattedMessage id="overview.users" />
                </NavLink>
              </Visibility>
            )}
            {hasMasterDataViewerRole && (
              <Visibility roles={[MASTER_DATA_VIEWER]}>
                <NavLink as={Link} to="/userArea/masterDataManagement" eventKey="masterDataManagement">
                <FontAwesomeIcon
                    icon={faFolderTree}
                    title={intl.formatMessage({ id: "overview.masterdata" })}
                    className="me-1"
                    size="lg"
                  />
                  {!navIconsOnly && <TextWithBreakpoint id="overview.masterdata" />}
                </NavLink>
              </Visibility>
            )}
            {hasRoleEditorRole && (
              <Visibility roles={[ROLE_EDITOR]}>
                <NavLink as={Link} to="/userArea/roleManagement" eventKey="roleManagement">
                  <FormattedMessage id="overview.roles" />
                </NavLink>
              </Visibility>
            )}
          </>
        )}
      </>
    );

  return (
    <Navbar expand="xl" bg="light" className="px-3 py-2">
      <NavbarBrand href={UserUtils.getUserHome(roles)}>
        <DAADLogo />
      </NavbarBrand>
      <Navbar.Toggle aria-controls="navigation" />
      <NavbarCollapse id="navigation">
        <Nav className="me-auto" activeKey={page}>
          {isLoggedIn() && links}
        </Nav>
        <Nav activeKey={page}>
          {canClearSurveyCaches && (
            <NavLink
              as="button"
              type="button"
              className="bg-transparent border-0"
              onClick={clearSurveyCaches}
              title={intl.formatMessage({ id: "header.clear.survey.caches" })}
            >
              <FontAwesomeIcon icon={faDatabase} className="me-1" size="lg" />
              {!navIconsOnly && <FormattedMessage id="header.clear.survey.caches" />}
            </NavLink>
          )}
          {showMessage && (
            <NavLink
              as="button"
              type="button"
              className="text-warning bg-transparent border-0"
              onClick={showDowntimeNotification}
              title={intl.formatMessage({ id: "general.notification.downtime" })}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} size="lg" />
            </NavLink>
          )}
          {showHint && (
            <h3>
              <Badge bg="warning" text="bg-warning">
                <FormattedMessage id={showHint === "QS" ? "header.qa.message" : "header.dev.message"} />
              </Badge>
            </h3>
          )}
          <NavDropdown
            id="help"
            align="end"
            title={
              <>
                <FontAwesomeIcon icon={faQuestionCircle} className="me-1" size="lg" />
                <FormattedMessage id="app.header.help" />
              </>
            }
          >
            <NavDropdown.Item href="https://static.daad.de/media/daad_de/pdfs_nicht_barrierefrei/der-daad/wer-wir-sind/2024_07_handbuch_tool_hochschulen_v2.1.pdf" target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="overview.user.manual" />
            </NavDropdown.Item>
            <NavDropdown.Item href="https://static.daad.de/media/daad_de/pdfs_nicht_barrierefrei/der-daad/2026_01_handbuch_tool_lektorinnen_v1.0.pdf" target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="overview.user.manual.lecturer" />
            </NavDropdown.Item>
            {isLoggedIn() && (
              <NavDropdown.Item as={Link} to={"/help/faq.pdf"} target="_blank">
                <FormattedMessage id="overview.user.faq" />
              </NavDropdown.Item>
            )}
          </NavDropdown>
          <LanguageSelector />
          {isLoggedIn() && !temp_user && (
            <NavDropdown
              id="auth"
              align="end"
              title={<FontAwesomeIcon icon={faUser} className="me-1" size="lg" />}
              active={page === "profile"}
            >
              <NavDropdown.Item as={Link} to="/userArea/profile" eventKey="profile">
                <FormattedMessage id="overview.profile" />
              </NavDropdown.Item>
              {!(temp_user || pl || pa) && (
                <NavDropdown.Item onClick={doLogout}>
                  <FormattedMessage id="header.logout.button" />
                </NavDropdown.Item>
              )}
            </NavDropdown>
          )}
        </Nav>
      </NavbarCollapse>
    </Navbar>
  );
};

Header.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  clearSurveyCaches: PropTypes.func.isRequired,
  currentUsername: PropTypes.string,
  doLogout: PropTypes.func.isRequired,
  roles: PropTypes.array,
  showHint: PropTypes.string,
  showMessage: PropTypes.bool,
  showDowntimeNotification: PropTypes.func.isRequired,
  navIconsOnly: PropTypes.bool
};

export default injectIntl(Header);
